import { useState, useRef, useCallback, useEffect, KeyboardEvent, DragEvent, ClipboardEvent } from 'react';
import { IconButton, Icon } from '../ui';
import MentionDropdown from './MentionDropdown';
import AttachmentPreview from './AttachmentPreview';
import {
  useChatUpload,
  createPreview,
  getImageDimensions,
} from './useChatAttachments';
import type { PendingFile, UploadedFile } from './useChatAttachments';

interface MessageInputProps {
  channelId?: string;
  onSend: (content: string, attachments?: UploadedFile[]) => Promise<void>;
  onTyping?: () => void;
  placeholder?: string;
}

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

export default function MessageInput({ channelId, onSend, onTyping, placeholder: placeholderProp }: MessageInputProps) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMention, setShowMention] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);
  const { uploadFiles, uploading } = useChatUpload();

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 6 * 24; // ~6 lines
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- File handling ---

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const newPending: PendingFile[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`"${file.name}" exceeds the 30 MB limit.`);
        continue;
      }

      const dims = await getImageDimensions(file);
      newPending.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        preview: createPreview(file),
        widthPx: dims?.width ?? null,
        heightPx: dims?.height ?? null,
      });
    }

    if (newPending.length > 0) {
      setPendingFiles((prev) => [...prev, ...newPending]);
    }
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => {
      const removed = prev.find((pf) => pf.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((pf) => pf.id !== id);
    });
  }, []);

  // --- File picker ---

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
      }
      // Reset so the same file can be picked again
      e.target.value = '';
    },
    [addFiles]
  );

  // --- Drag and drop ---

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current -= 1;
    if (dragCountRef.current <= 0) {
      dragCountRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCountRef.current = 0;
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  // --- Paste from clipboard ---

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        addFiles(imageFiles);
      }
    },
    [addFiles]
  );

  // --- @mention detection ---

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    onTyping?.();

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex >= 0) {
      const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if (atIndex === 0 || /\s/.test(charBefore)) {
        const query = textBeforeCursor.substring(atIndex + 1);
        if (!query.includes(' ')) {
          setShowMention(true);
          setMentionQuery(query);
          setMentionStart(atIndex);
          return;
        }
      }
    }

    setShowMention(false);
    setMentionQuery('');
    setMentionStart(-1);
  };

  const handleMentionSelect = (user: { id: string; name: string }) => {
    if (mentionStart < 0) return;

    const before = value.substring(0, mentionStart);
    const cursorPos = textareaRef.current?.selectionStart || value.length;
    const after = value.substring(cursorPos);
    const newValue = `${before}@${user.name} ${after}`;

    setValue(newValue);
    setShowMention(false);
    setMentionQuery('');
    setMentionStart(-1);

    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + user.name.length + 2;
        textareaRef.current.focus();
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
      }
    }, 0);
  };

  // --- Send ---

  const handleSend = async () => {
    const trimmed = value.trim();
    const hasFiles = pendingFiles.length > 0;

    // Need either text or files
    if ((!trimmed && !hasFiles) || sending) return;

    setSending(true);
    try {
      let uploaded: UploadedFile[] | undefined;

      // Upload files first if any
      if (hasFiles) {
        uploaded = await uploadFiles(channelId || 'thread', pendingFiles);
      }

      await onSend(trimmed, uploaded);

      // Clear state on success
      setValue('');
      // Revoke preview URLs
      pendingFiles.forEach((pf) => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
      setPendingFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMention) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = !value.trim() && pendingFiles.length === 0;

  return (
    <div
      className="relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{
            background: 'var(--accent-soft)',
            border: '2px dashed var(--border-focus)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: 'var(--accent-text)',
            }}
          >
            Drop files here
          </span>
        </div>
      )}

      {/* Attachment preview area */}
      <AttachmentPreview
        files={pendingFiles}
        onRemove={removeFile}
        uploading={uploading}
      />

      {/* Input area */}
      <div
        className="px-3 py-2.5"
        style={{ borderTop: '1px solid var(--border-hairline)' }}
      >
        {/* Mention dropdown */}
        <MentionDropdown
          channelId={channelId || ''}
          query={mentionQuery}
          onSelect={handleMentionSelect}
          onClose={() => setShowMention(false)}
          visible={showMention}
          anchorEl={textareaRef.current}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />

        <div className="flex items-end gap-2">
          {/* Attachment button */}
          <IconButton
            icon="paperclip"
            label="Attach files"
            onClick={openFilePicker}
            disabled={sending}
          />

          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholderProp || 'Type a message...'}
            rows={1}
            className="flex-1 resize-none outline-none"
            style={{
              maxHeight: '144px',
              padding: '7px 12px',
              background: 'var(--surface-raised)',
              border: isFocused
                ? '1px solid var(--border-focus)'
                : '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-base)',
              lineHeight: '20px',
              color: 'var(--text-body)',
              transition: 'border-color var(--dur-base) var(--ease-out)',
            }}
          />

          <button
            onClick={handleSend}
            disabled={isEmpty || sending}
            aria-label="Send message"
            title="Send message"
            className={`flex-shrink-0 pressable ${isEmpty || sending ? '' : 'hover-glow'}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--control-md)',
              height: 'var(--control-md)',
              border: '1px solid transparent',
              borderRadius: 'var(--radius-sm)',
              background: isEmpty || sending ? 'var(--surface-raised)' : 'var(--accent)',
              color: isEmpty || sending ? 'var(--text-faint)' : 'var(--text-on-accent)',
              cursor: isEmpty || sending ? 'not-allowed' : 'pointer',
              transition: 'background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
            }}
          >
            <Icon name="send" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
